import React from 'react';
import { Pagination } from 'react-bootstrap';

export type PageSizeChoice = number | 'auto';

interface IProps {
  currentPage: number;
  totalPages?: number;
  onPageChange: (page: number) => void;
  pageSize?: number;
  onPageSizeChange?: (size: PageSizeChoice) => void;
  isAutoPageSize?: boolean;
  fitPageSize?: number;
  total?: number;
  /** Rows on the current page, for the "Showing a-b of n" text. */
  itemCount?: number;
  noun?: string;
}

const ROW_HEIGHT = 48;

/** Height that keeps a card the same size on short pages and while loading. */
export const tableMinHeight = (rows: number, hasFilterRow: boolean) =>
  (hasFilterRow ? 86 : 42) + (rows || 10) * ROW_HEIGHT;

export const PAGE_SIZES = [10, 15, 25, 50];

// Table footer: rows-per-page selector on the left, compact pagination on the right.
const TablePagination: React.FC<IProps> = ({
  currentPage,
  totalPages = 0,
  onPageChange,
  pageSize,
  onPageSizeChange,
  isAutoPageSize = false,
  fitPageSize,
  total,
  itemCount = 0,
  noun = 'records',
}) => {
  const perPage = pageSize || itemCount;
  const from = total ? (currentPage - 1) * perPage + 1 : 0;
  const to = total ? from + itemCount - 1 : 0;
  const pages: (number | 'gap')[] = [];
  for (let page = 1; page <= totalPages; page += 1) {
    const nearCurrent = page >= currentPage - 2 && page <= currentPage + 2;
    if (page === 1 || page === totalPages || nearCurrent) {
      pages.push(page);
    } else if (pages[pages.length - 1] !== 'gap') {
      pages.push('gap');
    }
  }

  return (
    <div className="table-foot">
      {pageSize && onPageSizeChange ? (
        <label className="rows-per-page" htmlFor="rows-per-page">
          Rows per page
          <select
            id="rows-per-page"
            value={isAutoPageSize ? 'auto' : pageSize}
            onChange={(e) =>
              onPageSizeChange(
                e.target.value === 'auto' ? 'auto' : Number(e.target.value),
              )
            }
          >
            {fitPageSize ? (
              <option value="auto">Fit to screen ({fitPageSize})</option>
            ) : null}
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {typeof total === 'number' ? (
        <span className="table-foot-range">
          {total === 0
            ? `No ${noun}`
            : `Showing ${from}–${to} of ${total} ${noun}`}
        </span>
      ) : null}
      {totalPages > 1 ? (
        <Pagination>
          <Pagination.Prev
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1}
          >
            ‹
          </Pagination.Prev>
          {pages.map((page, index) =>
            page === 'gap' ? (
              // eslint-disable-next-line react/no-array-index-key
              <Pagination.Ellipsis key={`gap-${index}`} disabled />
            ) : (
              <Pagination.Item
                key={page}
                active={page === currentPage}
                onClick={() => onPageChange(page)}
              >
                {page}
              </Pagination.Item>
            ),
          )}
          <Pagination.Next
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
          >
            ›
          </Pagination.Next>
        </Pagination>
      ) : null}
    </div>
  );
};

export default TablePagination;
